import { useState, useEffect, useMemo } from 'react'

import css from './sass/upload.documents.module.scss'
import themeCss from '../components/sass/theme.module.scss'
import { setSessionItem } from '../components/utils'

import Cropper from 'react-easy-crop'


function ImageEditor ( {onSubmit, isEditing, setIsEditing, workingFile, setWorkingFile, imageType}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [minZoom, setMinZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)

  const [workingHeight, setWorkingHeight] = useState(0)
  const [workingWidth, setWorkingWidth] = useState(0)

  // use Memo to prevent infinite rendering for image source, only changes when workingFile changes
  const objectUrl = useMemo(() => {
    if (!workingFile) return null

    const image = new Image()
    image.src = URL.createObjectURL(workingFile)

    if (workingFile && image) {
      image.onload = () => {
        //assign height and width upon memoization to state variables for zoom calculations
        setWorkingHeight(image.height)
        setWorkingWidth(image.width)  
        } 
        image.onerror = (error) => {
          console.log('Error loading image:', error)
        }
      }
    return URL.createObjectURL(workingFile)
  }, [workingFile])

  useEffect(() => {
    if (!workingFile || imageType !== 'auction') return
    
    // Calculate the initial zoom level to fit the entire image in the cropper
    if (objectUrl) {
        const imageAspectRatio = workingWidth / workingHeight
        const cropperAspectRatio = 16 / 9

        // If the image is taller than the cropper aspect ratio, fit by height
        if (imageAspectRatio < cropperAspectRatio) {
          console.log('7')
          const minimumZoomCalcuation = workingWidth / (workingHeight * cropperAspectRatio)
          setMinZoom(minimumZoomCalcuation) 
          setZoom(minimumZoomCalcuation)
        } 
    }

  }, [workingFile, workingHeight, workingWidth])

  //use effect to center the image in the cropper if the zoom is less than 1
  useEffect(() => {

    if (zoom < 1) {
      let offsetX = 0
      let offsetY = crop.y

      const cropperAspectRatio = 16 / 9
      const scaledWidth = workingWidth * zoom
      const cropperWidth = workingHeight * cropperAspectRatio
      offsetX = (scaledWidth - cropperWidth) / 2 / zoom / 1000

      // to correct for centering the image in the cropper
      offsetX += 2
      
      setCrop({ x: -offsetX, y: offsetY })
    } else return
  }, [zoom])

  const handleCropChange = (newCrop) => {
    if (newCrop.x < 0 && zoom < 1) newCrop.x = crop.x
    setCrop(newCrop)
  }

  const zoomSliderChange = (e) => {
    setZoom(Number(e.target.value))
  }

  const getCroppedImg = async (imageSrc, croppedAreaPixels) => {
    return new Promise((resolve, reject) => {
      const image = new Image()
      // Convert the File object to an object URL
      const croppedUrl = URL.createObjectURL(imageSrc)      
      image.src = croppedUrl

      image.onload = () => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        // Set the canvas size to the cropped area
        canvas.width = croppedAreaPixels.width
        canvas.height = croppedAreaPixels.height
  
        ctx.drawImage(
          image,
          croppedAreaPixels.x, // Start x-coordinate of the crop
          croppedAreaPixels.y, // Start y-coordinate of the crop
          croppedAreaPixels.width, // Width of the crop
          croppedAreaPixels.height, // Height of the crop
          0, // Place the cropped image at x=0 on the canvas
          0, // Place the cropped image at y=0 on the canvas
          croppedAreaPixels.width, // Draw the cropped image with the same width
          croppedAreaPixels.height // Draw the cropped image with the same height
        )

        // Convert the canvas to a Blob
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Canvas is empty'))
            return
          }
  
          // Revoke the object URL to free memory
          URL.revokeObjectURL(croppedUrl)
          resolve(blob) // Return the cropped image as a Blob
        }, 'image/jpeg')
      }
      image.onerror = (error) => {
        // Revoke the object URL in case of an error
        URL.revokeObjectURL(croppedUrl)
        reject(error)
      }
    })
  }

  const handleCropComplete = (croppedArea, croppedAreaPixels) => {
    if (objectUrl){
      setCroppedAreaPixels(croppedAreaPixels)
    }
  }

  const saveEditedImage = async (e) => {
    if (!workingFile || !croppedAreaPixels) return

    try {
      // set session storage flag for image upload in progress using utility function
      setSessionItem('imageUploadInProgress', true)
      
      // Get the cropped image as a Blob
      const croppedImage = await getCroppedImg(workingFile, croppedAreaPixels)
      // Create a new File object from the cropped Blob
      const editedFile = new File([croppedImage], workingFile.name, { type: workingFile.type })

      setIsEditing(false)
      setWorkingFile(null)
      onSubmit(e, [editedFile])
    } catch (error) {
      console.error('Error cropping image:', error)
      // clear image upload flag on error
      setSessionItem('imageUploadInProgress', null)
    }
  }

  return (
    <div className={css.formStyles}>

      {isEditing && workingFile &&
      <div className={css['editor-container']}>
        <Cropper
          image={objectUrl}
          crop={crop}
          zoom={zoom}
          aspect={16 / 9}
          onCropChange={handleCropChange}
          onZoomChange={setZoom}
          onCropComplete={handleCropComplete}
          classes={{
            cropAreaClassName: css['custom-crop-area'],
          }}
        />
        <div className={css['editor-buttons']}>
          <button type="button" className={`${themeCss['button']} ${themeCss['FilledYellow']}`} onClick={saveEditedImage}>UPLOAD</button>
          <button type="button" className={`${themeCss['button']} ${themeCss['FilledYellow']}`} onClick={() => setIsEditing(false)}>CANCEL</button>
        </div>

        <div className={css['editor-instruction']}>
          <div className={css["zoom-slider-container"]}>
            <label>Zoom</label>
            <input
              id="zoom-range"
              type="range"
              min={imageType === 'auction' ? minZoom : 1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={zoomSliderChange}
              className={css["zoom-slider"]}
            />
          </div>
          <p>
            Drag the image to move it within the crop area.
          </p>
          <p>
            Use the slider, or pinch/scroll with two fingers to zoom in or out.
          </p>
          <p>
            Once the image is positioned as desired, click "UPLOAD" to add it to the auction.
          </p>
        </div>
      </div>
    }
    </div>
  )
}

export default ImageEditor
